
import { buildWorldPayload, forEachRoom } from "./worldJson";
import { getRoomFileUrls, WHITEBOARD_IMAGE_URL_TTL_SECONDS } from "./roomFileStorage";
import { sleep } from "./asyncHelper";
import { markUnityWorldLoading, takeIslandRemovedByUnity } from "./unityEvents";

export const UNITY_BRIDGE_GO =
  (typeof process !== "undefined" && process.env?.REACT_APP_UNITY_BRIDGE_GO) || "ReactBridge";

/** Unity logs this when sendMessage runs before the bridge GameObject exists. */
export function isReactBridgeMissingError(error) {
  const msg = String(error?.message ?? error ?? "").toLowerCase();
  return msg.includes("reactbridge not found") || msg.includes("object reactbridge not found");
}

export const UNITY_BRIDGE_METHODS = {
  SET_AUTH_DATA: "SetAuthData",
  SET_USER_PROFILE: "SetUserProfile",
  SYNC_WORLD_STATE: "SyncWorldState",
  SET_UNLOCK_STATE: "SetUnlockState",
  SET_ROOM_UNLOCK_STATE: "SetRoomUnlockState",
  FLOOR_UP: "FloorUp",
  FLOOR_DOWN: "FloorDown",
  GO_TO_FLOOR: "GoToFloor",
  RETURN_TO_INSPECTION: "ReturnToInspection",
  EXIT_INSPECTION: "ExitInspection",
  RETURN_TO_AERIAL: "ReturnToAerial",
  GO_TO_ROOM: "GoToRoom",
  GO_TO_HOTSPOT: "GoToHotspot",
  SET_MASTER_VOLUME: "SetMasterVolume",
  SET_MUSIC_VOLUME: "SetMusicVolume",
  SET_SFX_VOLUME: "SetSfxVolume",
  SET_MASTER_MUTED: "SetMasterMuted",
  SET_MUSIC_MUTED: "SetMusicMuted",
  SET_SFX_MUTED: "SetSfxMuted",
  CLEAR_WORLD_STATE: "ClearWorld",
  CLOSE_HOTSPOT: "CloseHotspot",
  SET_WHITEBOARD_IMAGE: "SetWhiteboardImage",
  SET_SURFACE_IMAGE: "SetSurfaceImage",
  NAVIGATE_HOTSPOT: "NavigateHotspot",
  HIGHLIGHT_BOOKS: "HighlightBooks",
  ADD_BOOK: "AddBook",
  REMOVE_BOOK: "RemoveBook",
  SET_BOOK_EDIT_MODE: "SetBookEditMode",
  SET_OPEN_BOOKS: "SetOpenBooks",
  RESTORE_SESSION: "RestoreSession",
  REQUEST_SESSION_STATE: "RequestSessionState",
  SET_ISLAND_UNLOCK_STATE: "SetIslandUnlockState",
  APPLY_WORLD_PATCH: "ApplyWorldPatch",
};

/** Remove map-only fields Unity does not need. */
export function stripMapOnlyFields(worldPayload) {
  const clone = JSON.parse(JSON.stringify(worldPayload || {}));
  for (const district of clone.districts || []) {
    for (const island of district.islands || []) {
      delete island.map_x;
      delete island.map_y;
      for (const building of island.buildings || []) {
        delete building.previewPosition;
        delete building.map_x;
        delete building.map_y;
      }
    }
  }
  return clone;
}

const LAYOUT_ONLY_KEYS = new Set(["layout", "map_x", "map_y", "previewPosition"]);

function structureJson(value) {
  return JSON.stringify(value, (key, v) => {
    if (LAYOUT_ONLY_KEYS.has(key)) return undefined;
    if (!v || typeof v !== "object" || Array.isArray(v)) return v;
    return Object.keys(v).sort().reduce((sorted, k) => {
      sorted[k] = v[k];
      return sorted;
    }, {});
  });
}

export function worldStructureKey(world) {
  return structureJson({ id: world?.id, name: world?.name, districts: world?.districts });
}

export function diffWorldPatches(prevDistricts, nextDistricts) {
  return compareWorlds(prevDistricts, nextDistricts).ops || null;
}

export function explainWorldDiff(prevDistricts, nextDistricts) {
  return compareWorlds(prevDistricts, nextDistricts).reason || null;
}

function changedKeys(before, after) {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  return [...keys].filter((k) => !LAYOUT_ONLY_KEYS.has(k) && structureJson(before?.[k]) !== structureJson(after?.[k]));
}

function omit(node, keys) {
  const copy = { ...node };
  for (const key of keys) delete copy[key];
  return copy;
}

const sameStructure = (a, b) => structureJson(a) === structureJson(b);

function nameChanged(before, after) {
  return (before?.name ?? "") !== (after?.name ?? "") && String(after?.name ?? "").trim() !== "";
}

function diffChildren(prevList, nextList, label) {
  const prevIds = (prevList || []).map((n) => n.id);
  const nextIds = (nextList || []).map((n) => n.id);
  const added = nextIds.filter((id) => !prevIds.includes(id));
  const removed = prevIds.filter((id) => !nextIds.includes(id));
  const kept = prevIds.filter((id) => nextIds.includes(id));
  if (kept.join("|") !== nextIds.filter((id) => prevIds.includes(id)).join("|")) {
    return { reason: `${label} were reordered` };
  }
  return { added, removed, kept };
}

function unlockChanged(before, after) {
  return (before?.is_unlocked !== false) !== (after?.is_unlocked !== false);
}

const BUILDING_PATCH_KEYS = ["name", "floors", "is_unlocked", "model_id", "colors"];

function compareBuilding(before, after, ops) {
  if (!sameStructure(omit(before, BUILDING_PATCH_KEYS), omit(after, BUILDING_PATCH_KEYS))) {
    return `building "${after.id}" changed (${changedKeys(omit(before, ["floors"]), omit(after, ["floors"])).join(", ")})`;
  }
  const data = {};
  if (nameChanged(before, after)) data.name = after.name;
  if (after.model_id && before.model_id !== after.model_id) data.model_id = after.model_id;
  if (after.colors && !sameStructure(before.colors, after.colors)) data.colors = after.colors;
  if (Object.keys(data).length) ops.push({ op: "update_building", buildingId: after.id, data });
  if (unlockChanged(before, after)) ops.push({ op: "set_building_unlock", id: after.id, unlocked: after.is_unlocked !== false });

  const floors = diffChildren(before.floors, after.floors, `floors of building "${after.id}"`);
  if (floors.reason) return floors.reason;
  if (floors.added.length || floors.removed.length) return `floors of building "${after.id}" were added or removed`;
  const prevFloors = new Map((before.floors || []).map((f) => [f.id, f]));
  for (const floor of after.floors || []) {
    const prevFloor = prevFloors.get(floor.id);
    if (!sameStructure(omit(prevFloor, ["name", "rooms"]), omit(floor, ["name", "rooms"]))) {
      return `floor "${floor.id}" changed`;
    }
    if (nameChanged(prevFloor, floor)) {
      ops.push({ op: "update_floor", buildingId: after.id, floorId: floor.id, data: { name: floor.name } });
    }
    const rooms = diffChildren(prevFloor.rooms, floor.rooms, `rooms of floor "${floor.id}"`);
    if (rooms.reason) return rooms.reason;
    if (rooms.added.length || rooms.removed.length) return `rooms of floor "${floor.id}" were added or removed`;
    const prevRooms = new Map((prevFloor.rooms || []).map((r) => [r.id, r]));
    for (const room of floor.rooms || []) {
      const prevRoom = prevRooms.get(room.id);
      if (!sameStructure(omit(prevRoom, ["name", "is_unlocked"]), omit(room, ["name", "is_unlocked"]))) return `room "${room.id}" changed`;
      if (nameChanged(prevRoom, room)) ops.push({ op: "update_room", roomId: room.id, data: { name: room.name } });
      if (unlockChanged(prevRoom, room)) ops.push({ op: "set_room_unlock", id: room.id, unlocked: room.is_unlocked !== false });
    }
  }
  return null;
}

function compareIsland(before, after, ops, removals) {
  if (!sameStructure(omit(before, ["name", "buildings", "is_unlocked"]), omit(after, ["name", "buildings", "is_unlocked"]))) {
    return `island "${after.id}" changed (${changedKeys(omit(before, ["buildings"]), omit(after, ["buildings"])).join(", ")})`;
  }
  if (nameChanged(before, after)) ops.push({ op: "update_island", islandId: after.id, data: { name: after.name } });
  if (unlockChanged(before, after)) ops.push({ op: "set_island_unlock", id: after.id, unlocked: after.is_unlocked !== false });

  const buildings = diffChildren(before.buildings, after.buildings, `buildings of island "${after.id}"`);
  if (buildings.reason) return buildings.reason;
  if (buildings.added.length) return `buildings were added to island "${after.id}"`;
  for (const buildingId of buildings.removed) removals.push({ op: "remove_building", buildingId });

  const prevBuildings = new Map((before.buildings || []).map((b) => [b.id, b]));
  for (const building of after.buildings || []) {
    const reason = compareBuilding(prevBuildings.get(building.id), building, ops);
    if (reason) return reason;
  }
  return null;
}

function compareWorlds(prevDistricts, nextDistricts) {
  if (!Array.isArray(prevDistricts)) return { reason: "no world sent to Unity yet" };
  if (!Array.isArray(nextDistricts) || !nextDistricts.length) return { reason: "world is empty" };

  const prevById = new Map(prevDistricts.map((d) => [d.id, d]));
  const nextIds = new Set(nextDistricts.map((d) => d.id));
  const addedDistricts = nextDistricts.filter((d) => !prevById.has(d.id)).map((d) => d.id);
  if (addedDistricts.length) return { reason: `districts were added: [${addedDistricts}]` };
  const removedDistricts = prevDistricts.filter((d) => !nextIds.has(d.id));

  const prevIslands = new Map();
  for (const d of prevDistricts) {
    for (const island of d.islands || []) prevIslands.set(island.id, { districtId: d.id, island });
  }

  const removals = removedDistricts.map((d) => ({ op: "remove_district", districtId: d.id }));
  const updates = [];
  const additions = [];
  const seen = new Set(removedDistricts.flatMap((d) => (d.islands || []).map((i) => i.id)));

  for (const d of nextDistricts) {
    const prev = prevById.get(d.id);
    if (!sameStructure(omit(prev, ["islands", "name", "color"]), omit(d, ["islands", "name", "color"]))) {
      return { reason: `district "${d.id}" changed (${changedKeys(omit(prev, ["islands"]), omit(d, ["islands"])).join(", ")})` };
    }
    const districtData = {};
    if (nameChanged(prev, d)) districtData.name = d.name;
    if ((prev.color ?? "") !== (d.color ?? "") && d.color) districtData.color = d.color;
    if (Object.keys(districtData).length) updates.push({ op: "update_district", districtId: d.id, data: districtData });

    for (const island of d.islands || []) {
      seen.add(island.id);
      const before = prevIslands.get(island.id);
      if (!before) {
        additions.push({ op: "add_island", districtId: d.id, islandId: island.id });
        continue;
      }
      if (before.districtId !== d.id) return { reason: `island "${island.id}" moved to another district` };
      const reason = compareIsland(before.island, island, updates, removals);
      if (reason) return { reason };
    }
  }
  for (const islandId of prevIslands.keys()) {
    if (!seen.has(islandId)) removals.push({ op: "remove_island", islandId });
  }
  const order = { remove_district: 0, remove_island: 1, remove_building: 2 };
  removals.sort((a, b) => order[a.op] - order[b.op]);
  return { ops: [...removals, ...updates, ...additions] };
}

export function buildSetAuthDataPayload(user, session) {
  if (!user) return null;
  return {
    userId: user.id,
    accessToken: session?.access_token || "",
    email: user.email || "",
  };
}

export function buildSetUserProfilePayload({ user, appUser, userProfile }) {
  if (!user && !userProfile) return null;
  const avatar = userProfile?.avatar || {};
  return {
    id: user?.id || userProfile?.user_id,
    name: appUser?.name || avatar.name || user?.email?.split("@")[0] || "",
    email: appUser?.email || user?.email || "",
    avatar,
    declared_motivation: userProfile?.declared_motivation,
    declared_time_perception: userProfile?.declared_time_perception,
    declared_chronotype: userProfile?.declared_chronotype,
    declared_initiation: userProfile?.declared_initiation,
    declared_hyperfocus: userProfile?.declared_hyperfocus,
    declared_rsd_sensitivity: userProfile?.declared_rsd_sensitivity,
    declared_primary_challenge: userProfile?.declared_primary_challenge,
    declared_focus_environment: userProfile?.declared_focus_environment,
    declared_recovery: userProfile?.declared_recovery,
    declared_structure: userProfile?.declared_structure,
    skipped_quiz: userProfile?.skipped_quiz ?? false,
  };
}

const IMAGE_SURFACES = ["whiteboard", "bulletin_board"];
const surfaceKey = (item) => `${item.surface}:${item.room_id}`;

export function buildSyncWorldStatePayload(hierarchy, surfaceItems, bookshelfDocs) {
  const world = stripMapOnlyFields(buildWorldPayload(hierarchy, surfaceItems, bookshelfDocs));
  world.surface_items = (world.surface_items || []).map((item) => {
    if (!IMAGE_SURFACES.includes(item?.surface)) return item;
    const { image_path, image_updated_at, notes_signature, ...rest } = item;
    return { ...rest, content: "" };
  });
  return world;
}

/** Each room's whiteboard and bulletin board image, as a fresh signed URL, put into every item of that surface. */
export async function withSurfaceImageUrls(world, surfaceItems, signUrls) {
  const latestByRoom = new Map();
  for (const item of surfaceItems || []) {
    if (!IMAGE_SURFACES.includes(item?.surface) || !item.image_path || !item.room_id) continue;
    const current = latestByRoom.get(surfaceKey(item));
    if (!current || Number(item.image_updated_at || 0) > Number(current.image_updated_at || 0)) {
      latestByRoom.set(surfaceKey(item), item);
    }
  }
  if (!latestByRoom.size || typeof signUrls !== "function") return world;

  let urls;
  try {
    urls = await signUrls([...latestByRoom.values()].map((item) => item.image_path));
  } catch (error) {
    console.warn("[NeuroQuest → Unity] could not sign surface images", error?.message || error);
    return world;
  }

  const contentByRoom = new Map();
  for (const [key, item] of latestByRoom) {
    const url = urls?.get?.(item.image_path);
    if (!url) continue;
    const joiner = url.includes("?") ? "&" : "?";
    contentByRoom.set(key, `${url}${joiner}t=${encodeURIComponent(item.image_updated_at || 0)}`);
  }

  return {
    ...world,
    surface_items: (world.surface_items || []).map((item) =>
      item?.room_id && contentByRoom.has(surfaceKey(item))
        ? { ...item, content: contentByRoom.get(surfaceKey(item)) }
        : item
    ),
  };
}

export async function buildUnityWorld(hierarchy, surfaceItems, bookshelfDocs) {
  return withSurfaceImageUrls(
    buildSyncWorldStatePayload(hierarchy, surfaceItems, bookshelfDocs),
    surfaceItems,
    (paths) => getRoomFileUrls(paths, WHITEBOARD_IMAGE_URL_TTL_SECONDS)
  );
}

const UNLOCK_METHODS = {
  set_island_unlock: UNITY_BRIDGE_METHODS.SET_ISLAND_UNLOCK_STATE,
  set_building_unlock: UNITY_BRIDGE_METHODS.SET_UNLOCK_STATE,
  set_room_unlock: UNITY_BRIDGE_METHODS.SET_ROOM_UNLOCK_STATE,
};

export async function pushWorldPatches(sendMessage, ops, unityWorld) {
  const sent = [];
  for (const op of ops || []) {
    if (op.op === "remove_island" && takeIslandRemovedByUnity(op.islandId)) continue;
    if (UNLOCK_METHODS[op.op]) {
      const result = await safeSendBridgeMessage(
        sendMessage,
        UNLOCK_METHODS[op.op],
        { id: op.id, unlocked: op.unlocked },
        { retry: false }
      );
      if (!result.ok) return { ok: false, sent, error: result.error };
      sent.push(result);
      continue;
    }
    let patch = op;
    if (op.op === "add_island") {
      const district = (unityWorld?.districts || []).find((d) => d.id === op.districtId);
      const island = (district?.islands || []).find((i) => i.id === op.islandId);
      if (!island) return { ok: false, sent, error: new Error(`island ${op.islandId} missing from world payload`) };
      const roomIds = new Set();
      forEachRoom([{ islands: [island] }], (room) => roomIds.add(room.id));
      const surfaceItems = (unityWorld.surface_items || []).filter((item) => roomIds.has(item.room_id));
      patch = { op: "add_island", districtId: op.districtId, data: island, surfaceItems };
    }
    const result = await safeSendBridgeMessage(
      sendMessage,
      UNITY_BRIDGE_METHODS.APPLY_WORLD_PATCH,
      patch,
      { retry: false }
    );
    if (!result.ok) return { ok: false, sent, error: result.error };
    sent.push(result);
  }
  return { ok: true, sent };
}

export function sendBridgeMessage(sendMessage, method, data) {
  if (!sendMessage) {
    throw new Error("Unity sendMessage unavailable");
  }
  try {
    if (data === undefined) {
      sendMessage(UNITY_BRIDGE_GO, method);
      return { gameObject: UNITY_BRIDGE_GO, method, argument: null };
    }
    const arg =
      typeof data === "string"
        ? data
        : data == null
          ? ""
          : JSON.stringify(data);
    sendMessage(UNITY_BRIDGE_GO, method, arg);
    return { gameObject: UNITY_BRIDGE_GO, method, argument: arg };
  } catch (error) {
    const wrapped = error instanceof Error ? error : new Error(String(error));
    wrapped.bridgeMethod = method;
    throw wrapped;
  }
}

const BRIDGE_RETRY_ATTEMPTS = 6;
const BRIDGE_RETRY_DELAY_MS = 450;

/** SendMessage throws if ReactBridge is not ready yet (common right after isLoaded). */
export async function safeSendBridgeMessage(
  sendMessage,
  method,
  data,
  { retry = true, attempts, delayMs, silent = false } = {}
) {
  if (!sendMessage) {
    return { ok: false, method, error: new Error("Unity sendMessage unavailable") };
  }

  const maxAttempts = attempts ?? (retry ? BRIDGE_RETRY_ATTEMPTS : 1);
  const pauseMs = delayMs ?? BRIDGE_RETRY_DELAY_MS;
  let lastError = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const result = sendBridgeMessage(sendMessage, method, data);
      return { ok: true, ...result };
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts - 1) {
        await sleep(pauseMs);
      }
    }
  }

  if (
    process.env.NODE_ENV === "development" &&
    !silent &&
    !isReactBridgeMissingError(lastError)
  ) {
    console.warn(
      `[NeuroQuest → Unity] ReactBridge.${method} failed after ${maxAttempts} attempt(s)`,
      lastError
    );
  }
  return { ok: false, method, error: lastError };
}

/** React → Unity commands for building inspection navigation and unlock. */
export function createUnityBridgeCommands(sendMessage) {
  if (!sendMessage) return null;
  const cmd = (method, data) => {
    try {
      return sendBridgeMessage(sendMessage, method, data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn(`[NeuroQuest → Unity] ReactBridge.${method} failed`, error);
      }
      return null;
    }
  };
  return {
    floorUp: () => cmd(UNITY_BRIDGE_METHODS.FLOOR_UP),
    floorDown: () => cmd(UNITY_BRIDGE_METHODS.FLOOR_DOWN),
    goToFloor: (index) => cmd(UNITY_BRIDGE_METHODS.GO_TO_FLOOR, String(index)),
    returnToInspection: () => cmd(UNITY_BRIDGE_METHODS.RETURN_TO_INSPECTION),
    exitInspection: () => cmd(UNITY_BRIDGE_METHODS.EXIT_INSPECTION),
    returnToAerial: () => cmd(UNITY_BRIDGE_METHODS.RETURN_TO_AERIAL),
    goToRoom: (roomId) => cmd(UNITY_BRIDGE_METHODS.GO_TO_ROOM, String(roomId)),
    goToHotspot: (hotspotId) => cmd(UNITY_BRIDGE_METHODS.GO_TO_HOTSPOT, String(hotspotId)),
    closeHotspot: () => cmd(UNITY_BRIDGE_METHODS.CLOSE_HOTSPOT, ""),
    setWhiteboardImage: (roomId, content) =>
      cmd(UNITY_BRIDGE_METHODS.SET_WHITEBOARD_IMAGE, JSON.stringify({ room_id: String(roomId), content: content || "" })),
    setSurfaceImage: (roomId, surface, content) =>
      cmd(UNITY_BRIDGE_METHODS.SET_SURFACE_IMAGE, JSON.stringify({ room_id: String(roomId), surface, content: content || "" })),
    navigateHotspot: (direction) =>
      cmd(UNITY_BRIDGE_METHODS.NAVIGATE_HOTSPOT, direction === "left" ? "left" : "right"),
    highlightBooks: (entries) =>
      cmd(UNITY_BRIDGE_METHODS.HIGHLIGHT_BOOKS, JSON.stringify(entries || [])),
    addBook: (book) =>
      cmd(UNITY_BRIDGE_METHODS.ADD_BOOK, JSON.stringify({
        id: book.id,
        title: book.title || "Untitled",
        type: book.type,
      })),
    removeBook: (bookId) => cmd(UNITY_BRIDGE_METHODS.REMOVE_BOOK, String(bookId)),
    setBookEditMode: (enabled) =>
      cmd(UNITY_BRIDGE_METHODS.SET_BOOK_EDIT_MODE, enabled ? "true" : "false"),
    setOpenBooks: (bookIds) =>
      cmd(UNITY_BRIDGE_METHODS.SET_OPEN_BOOKS, JSON.stringify(bookIds || [])),
    setRoomUnlockState: (id, unlocked) =>
      cmd(UNITY_BRIDGE_METHODS.SET_ROOM_UNLOCK_STATE, { id, unlocked }),
    setBuildingUnlockState: (id, unlocked) =>
      cmd(UNITY_BRIDGE_METHODS.SET_UNLOCK_STATE, { id, unlocked }),
    /** Send the saved SessionState back as-is; null sends "" so Unity uses the default spot. */
    restoreSession: (state) =>
      cmd(UNITY_BRIDGE_METHODS.RESTORE_SESSION, state ? JSON.stringify(state) : ""),
    requestSessionState: () => cmd(UNITY_BRIDGE_METHODS.REQUEST_SESSION_STATE, ""),
  };
}

/** Push building + room unlock flags without reloading the city (safe mid-session). */
export async function pushWorldUnlockStates(sendMessage, districts) {
  if (!sendMessage || !districts?.length) return { ok: false, sent: 0 };

  const buildings = new Map();
  const unlockedRooms = [];
  forEachRoom(districts, (room, building) => {
    if (!buildings.has(building.id)) {
      buildings.set(building.id, building.is_unlocked !== false);
    }
    if (room.is_unlocked) {
      unlockedRooms.push(room.id);
    }
  });

  let sent = 0;
  for (const [id, unlocked] of buildings) {
    const result = await safeSendBridgeMessage(
      sendMessage,
      UNITY_BRIDGE_METHODS.SET_UNLOCK_STATE,
      { id, unlocked },
      { retry: false }
    );
    if (result.ok) sent += 1;
  }
  for (const roomId of unlockedRooms) {
    const result = await safeSendBridgeMessage(
      sendMessage,
      UNITY_BRIDGE_METHODS.SET_ROOM_UNLOCK_STATE,
      { id: roomId, unlocked: true },
      { retry: false }
    );
    if (result.ok) sent += 1;
  }

  return { ok: sent > 0, sent };
}

export async function pushEmergencyClearWorld(sendMessage, isNewUser = false) {
  return safeSendBridgeMessage(
    sendMessage,
    UNITY_BRIDGE_METHODS.CLEAR_WORLD_STATE,
    isNewUser ? "true" : "false",
    { attempts: 4, delayMs: 500, silent: true }
  );
}

/**
 * Recommended load order: SetAuthData → SetUserProfile → SyncWorldState or ClearWorld.
 */
export async function pushUnityBridgeSession({
  sendMessage,
  user,
  session,
  appUser,
  userProfile,
  hierarchy,
  surfaceItems,
  bookshelfDocs,
  syncWorld = true,
  isNewUser = false,
}) {
  const sent = [];
  const failures = [];

  try {
    const auth = buildSetAuthDataPayload(user, session);
    if (auth) {
      const result = await safeSendBridgeMessage(sendMessage, UNITY_BRIDGE_METHODS.SET_AUTH_DATA, auth, {
        attempts: 8,
        delayMs: 450,
        silent: true,
      });
      if (result.ok) sent.push(result);
      else failures.push(result);
    }

    const profile = buildSetUserProfilePayload({ user, appUser, userProfile });
    if (profile?.id) {
      const result = await safeSendBridgeMessage(sendMessage, UNITY_BRIDGE_METHODS.SET_USER_PROFILE, profile, {
        attempts: 6,
        delayMs: 400,
        silent: true,
      });
      if (result.ok) sent.push(result);
      else failures.push(result);
    }

    const world = await buildUnityWorld(hierarchy, surfaceItems, bookshelfDocs);
    const hasDistricts = (world.districts || []).length > 0;
    let worldSynced = false;

    if (syncWorld) {
      markUnityWorldLoading();
      if (hasDistricts) {
        // Has work data → SyncWorldState
        const result = await safeSendBridgeMessage(
          sendMessage,
          UNITY_BRIDGE_METHODS.SYNC_WORLD_STATE,
          world,
          { attempts: 12, delayMs: 600, silent: true }
        );
        if (result.ok) { sent.push(result); worldSynced = true; }
        else failures.push(result);
      } else {
        // No data → ClearWorld("true" for new user, "false" for existing user)
        const result = await safeSendBridgeMessage(
          sendMessage,
          UNITY_BRIDGE_METHODS.CLEAR_WORLD_STATE,
          isNewUser ? "true" : "false",
          { attempts: 12, delayMs: 600, silent: true }
        );
        if (result.ok) { sent.push(result); worldSynced = true; }
        else failures.push(result);
      }
    }

    return {
      sent,
      failures,
      ok: failures.length === 0,
      worldSynced,
      worldRoomCount: countRooms(world.districts),
      surfaceItemCount: world.surface_items?.length ?? 0,
      districts: world.districts || [],
    };
  } catch (error) {
    return {
      sent,
      failures: [...failures, { ok: false, method: "pushUnityBridgeSession", error }],
      ok: false,
      worldSynced: false,
      worldRoomCount: 0,
      surfaceItemCount: 0,
      districts: [],
    };
  }
}

function countRooms(districts) {
  let n = 0;
  for (const d of districts || []) {
    for (const i of d.islands || []) {
      for (const b of i.buildings || []) {
        for (const f of b.floors || []) {
          n += (f.rooms || []).length;
        }
      }
    }
  }
  return n;
}

/** Parse Unity → React wire format: { event, payload } */
export function parseUnityOutgoingMessage(message) {
  const raw = typeof message === "string" ? message : JSON.stringify(message);
  try {
    const parsed = typeof message === "string" ? JSON.parse(message) : message;
    if (parsed?.event) {
      return { event: parsed.event, payload: parsed.payload ?? null, raw };
    }
    if (parsed?.eventName) {
      return { event: parsed.eventName, payload: parsed.jsonData ?? parsed.payload ?? null, raw };
    }
  } catch {
    // fall through
  }
  return { event: null, payload: message, raw };
}
